/**
 * Muskelformen der Koerperkarte (F-34 C2) - eigene Zeichnung im Stil einer
 * anatomischen Muskeltafel: natuerliche Silhouette mit Haenden und Fuessen,
 * jeder Muskel eine weich gerundete Flaeche, dazwischen schmale Fugen in
 * Hautfarbe. Vorder- und Rueckseite je 240 × 520; jede Form ist fuer die
 * linke Bildhaelfte gezeichnet und wird an x = 120 gespiegelt.
 */
import type { MuskelId } from '@/domain/training/muskeln';

export const BREITE = 240;
export const HOEHE = 520;

/** Umriss der linken Koerperhaelfte ohne Kopf: Hals, Arm mit Hand, Rumpf, Bein mit Fuss. */
export const SILHOUETTE = [
  'M120,56 L109,58 C109,67 108,74 104,78 C96,82 82,82 72,86 C62,90 56,100 55,112',
  'C53,130 50,150 48,170 C46,185 44,195 43,205 C40,225 36,245 34,262',
  'C32,278 26,294 25,308 C24,318 30,327 36,325 C42,322 44,312 45,302 C47,292 48,282 46,272',
  'C50,250 56,232 60,214 C63,200 64,190 66,182 C70,165 74,140 78,118 L80,112',
  'C82,135 84,160 86,180 C88,195 88,210 86,222 C83,234 78,244 77,256',
  'C75,290 76,330 82,362 C84,372 83,380 82,388 C80,410 84,440 90,465 C92,474 92,482 90,488',
  'C86,496 84,504 88,509 L108,510 C112,508 112,500 110,494 C108,486 107,478 108,470',
  'C110,440 112,410 110,390 C110,380 110,372 112,362 C114,330 117,300 120,276 Z',
].join(' ');

export const KOPF = { cx: 120, cy: 34, rx: 18, ry: 23 };
export const OHR = { cx: 101, cy: 37, rx: 3.2, ry: 6 };

type Formen = Partial<Record<MuskelId, string[]>>;
type P = readonly [number, number];
type Linie = readonly P[];

const r1 = (v: number) => Math.round(v * 10) / 10;
const rueck = (l: Linie): Linie => [...l].reverse();

/**
 * Geschlossene, weiche Flaeche aus Grenzlinien. Jede Linie wird fuer sich als
 * Catmull-Rom-Kurve gezeichnet; benachbarte Muskeln nutzen dieselbe Linie
 * (eine davon rueckwaerts) und liegen so ohne Luecke aneinander - die Fuge
 * entsteht allein durch die Kontur in Hautfarbe.
 */
function flaeche(...linien: Linie[]): string {
  const start = linien[0]![0]!;
  let d = `M${r1(start[0])},${r1(start[1])}`;
  for (const l of linien) {
    for (let i = 0; i < l.length - 1; i += 1) {
      const p0 = l[i - 1] ?? l[i]!;
      const p1 = l[i]!;
      const p2 = l[i + 1]!;
      const p3 = l[i + 2] ?? p2;
      const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
      const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
      d += ` C${r1(c1[0]!)},${r1(c1[1]!)} ${r1(c2[0]!)},${r1(c2[1]!)} ${r1(p2[0])},${r1(p2[1])}`;
    }
  }
  return `${d} Z`;
}

/** Abgerundetes Rechteck - fuer die Felder des geraden Bauchmuskels. */
const feld = (x: number, y: number, b: number, h: number, r = 3) =>
  `M${x + r},${y} L${x + b},${y} L${x + b},${y + h} L${x + r},${y + h} Q${x},${y + h} ${x},${y + h - r} L${x},${y + r} Q${x},${y} ${x + r},${y} Z`;

// --- Grenzlinien: Schulter und Arm (vorne und hinten gleich) ---------------
const DELT_AUSSEN: Linie = [[74, 89], [64, 94], [58, 104], [56, 116], [56, 128], [57, 140]];
const DELT_TEIL: Linie = [[74, 89], [67, 100], [63, 118], [64, 146]];
const DELT_VORNE: Linie = [[86, 92], [85, 100], [83, 107], [80, 114], [75, 126], [64, 146]];
const SCHLUESSELBEIN: Linie = [[74, 89], [80, 89], [86, 92]];
const DELT_UNTEN: Linie = [[57, 140], [60, 143], [64, 146]];
const OBERARM_AUSSEN: Linie = [[57, 140], [52, 158], [50, 174], [46.5, 194]];
const OBERARM_INNEN: Linie = [[75, 126], [72, 146], [69.5, 160], [66.5, 178], [64, 192]];
const OBERARM_TEIL: Linie = [[64, 146], [60, 160], [58, 176], [55, 196]];
const UNTERARM_AUSSEN: Linie = [[46.5, 194], [44.5, 210], [41, 230], [38, 248], [36, 262]];
const UNTERARM_INNEN: Linie = [[64, 192], [61.5, 205], [58, 220], [53, 240], [48, 262]];
const UNTERARM_TEIL: Linie = [[55, 196], [50, 214], [45, 236], [42, 264]];

const schulterSeite = flaeche(DELT_AUSSEN, DELT_UNTEN, rueck(DELT_TEIL));
const schulterFront = flaeche(SCHLUESSELBEIN, DELT_VORNE, rueck(DELT_TEIL));
const oberarmInnen = flaeche(OBERARM_TEIL, [[55, 196], [64, 192]], rueck(OBERARM_INNEN), [[75, 126], [64, 146]]);
const oberarmAussen = flaeche(OBERARM_AUSSEN, [[46.5, 194], [55, 196]], rueck(OBERARM_TEIL), rueck(DELT_UNTEN));
const unterarm = [
  flaeche(UNTERARM_TEIL, [[42, 264], [48, 262]], rueck(UNTERARM_INNEN), [[64, 192], [55, 196]]),
  flaeche(UNTERARM_AUSSEN, [[36, 262], [42, 264]], rueck(UNTERARM_TEIL), [[55, 196], [46.5, 194]]),
];

// --- Brust ----------------------------------------------------------------
const BRUST_OBEN: Linie = [[118.5, 91], [106, 89], [94, 90], [86, 92]];
const BRUST_RAND_1: Linie = [[86, 92], [85, 100], [83, 107]];
const BRUST_RAND_2: Linie = [[83, 107], [80, 114], [81, 126]];
const BRUST_RAND_3: Linie = [[81, 126], [86, 138], [94, 145], [104, 148], [118.5, 147]];
const BRUST_TEIL_1: Linie = [[118.5, 108], [104, 107], [92, 108], [83, 107]];
const BRUST_TEIL_2: Linie = [[118.5, 127], [104, 127], [92, 127], [81, 126]];

// --- Oberschenkel vorne ---------------------------------------------------
const SCHENKEL_AUSSEN: Linie = [[79, 250], [77.5, 270], [77, 292]];
const SCHENKEL_AUSSEN_2: Linie = [[77, 292], [77.5, 312], [78.5, 332], [81, 350], [84.5, 368]];
const SCHENKEL_INNEN: Linie = [[118.5, 280], [116.8, 292], [114.6, 312], [112.2, 336]];
const SCHENKEL_INNEN_2: Linie = [[112.2, 336], [111.2, 352], [109.5, 368]];
const QUAD_TEIL_1: Linie = [[88, 252], [90, 265], [91, 278], [90, 295]];
const QUAD_TEIL_1B: Linie = [[90, 295], [90, 322], [91, 347], [93, 370]];
const QUAD_TEIL_2: Linie = [[106, 268], [104, 290], [102, 312], [101, 330]];
const QUAD_TEIL_2B: Linie = [[101, 330], [100.5, 350], [100, 370]];
const TFL_UNTEN: Linie = [[77, 292], [83, 296], [90, 295]];
const HUEFTBEUGER: Linie = [[106, 268], [100, 276], [91, 278]];
const INNEN_SCHNITT: Linie = [[101, 330], [106, 328], [112.2, 336]];

// --- Unterschenkel --------------------------------------------------------
const WADE_AUSSEN: Linie = [[83.5, 391], [82.6, 410], [83.5, 425], [85.5, 440], [88, 450], [91, 464]];
const WADE_INNEN: Linie = [[108.5, 391], [110.3, 410], [110, 425], [108.6, 440], [108, 450], [106.6, 468]];
const SCHIENBEIN: Linie = [[95, 391], [94.5, 412], [94, 430], [93, 448], [91, 464]];
const WADE_VORNE: Linie = [[99, 391], [98.5, 412], [98, 426], [97.8, 440], [97.4, 455], [97, 468]];
const WADE_VORNE_SCHNITT: Linie = [[97.8, 440], [103, 444], [108.6, 440]];

export const VORNE: Formen = {
  trapez_oben: ['M107,68 C104,76 96,82 76,87 C88,88 100,87 108,84 Z'],
  schulter_seite: [schulterSeite],
  schulter_vorne: [schulterFront],
  brust_oben: [flaeche(BRUST_OBEN, BRUST_RAND_1, rueck(BRUST_TEIL_1))],
  brust_mitte: [flaeche(BRUST_TEIL_1, BRUST_RAND_2, rueck(BRUST_TEIL_2))],
  brust_unten: [flaeche(BRUST_TEIL_2, BRUST_RAND_3)],
  serratus: [
    'M85,148 C84,152 84,155 85,158 C89,157 93,155 96,153 C93,150 89,148 85,148 Z',
    'M86,161 C85,165 85,168 86,171 C90,170 94,168 97,166 C94,163 90,161 86,161 Z',
    'M87,174 C86,177 86,180 87,182 C91,182 95,180 98,178 C95,176 91,174 87,174 Z',
  ],
  bauch_gerade: [
    feld(105, 150, 13, 17), feld(105, 170, 13, 18), feld(105, 191, 13, 18), feld(106, 212, 12, 18),
    'M107,233 L118,233 L118,268 C112,264 109,250 107,233 Z',
  ],
  bauch_schraeg: ['M89,186 C95,184 100,186 103,190 L103,232 C98,240 92,244 82,246 C84,236 86,226 87,214 C88,204 88,194 89,186 Z'],
  bizeps: [oberarmInnen],
  brachialis: [oberarmAussen],
  unterarm_beuger: unterarm,
  hueftbeuger: [flaeche([[88, 252], [97, 258], [106, 268]], HUEFTBEUGER, rueck([[88, 252], [90, 265], [91, 278]]))],
  abduktoren: [flaeche(SCHENKEL_AUSSEN, TFL_UNTEN, rueck(QUAD_TEIL_1), [[88, 252], [79, 250]])],
  adduktoren: [flaeche([[106, 268], [118.5, 280]], SCHENKEL_INNEN, rueck(INNEN_SCHNITT), rueck(QUAD_TEIL_2))],
  quad_aussen: [flaeche(SCHENKEL_AUSSEN_2, [[84.5, 368], [93, 370]], rueck(QUAD_TEIL_1B), rueck(TFL_UNTEN))],
  quad_gerade: [flaeche([[91, 278], [90, 295]], QUAD_TEIL_1B, [[93, 370], [100, 370]], rueck(QUAD_TEIL_2B), rueck(QUAD_TEIL_2), HUEFTBEUGER)],
  quad_innen: [flaeche(INNEN_SCHNITT, SCHENKEL_INNEN_2, [[109.5, 368], [100, 370]], rueck(QUAD_TEIL_2B))],
  schienbein: [flaeche(WADE_AUSSEN.slice(0, 6), rueck(SCHIENBEIN), [[95, 391], [83.5, 391]])],
  waden_zwilling: [flaeche([[99, 391], [108.5, 391]], WADE_INNEN.slice(0, 4), rueck(WADE_VORNE_SCHNITT), rueck(WADE_VORNE.slice(0, 4)))],
  waden_scholle: [flaeche(WADE_VORNE_SCHNITT, WADE_INNEN.slice(3), [[106.6, 468], [97, 468]], rueck(WADE_VORNE.slice(3)))],
};

// --- Rueckseite: Gesaess, Beinbeuger, Waden -------------------------------
const GESAESS_OBEN: Linie = [[118.5, 251], [108, 248], [96, 247], [84, 250]];
const GESAESS_AUSSEN: Linie = [[84, 250], [80, 258], [78.5, 272], [78.5, 286], [80, 296]];
const GESAESS_UNTEN: Linie = [[80, 296], [90, 303], [104, 305], [115.5, 302]];
const BEUGER_AUSSEN: Linie = [[80, 296], [77.6, 312], [78.6, 332], [81, 350], [84, 370]];
const BEUGER_INNEN: Linie = [[115.5, 302], [114.6, 312], [112.6, 332]];
const BEUGER_INNEN_2: Linie = [[112.6, 332], [111.4, 350], [110.8, 370]];
const BEUGER_TEIL_1: Linie = [[90, 303], [91, 325], [92, 348], [94, 371]];
const BEUGER_TEIL_2: Linie = [[104, 305], [105, 318], [105, 330]];
const BEUGER_TEIL_2B: Linie = [[105, 330], [104, 346], [102, 371.5]];
const ADDUKTOR_SCHNITT: Linie = [[112.6, 332], [108, 331], [105, 330]];
const WADE_MITTE: Linie = [[97, 393], [96.6, 420], [96, 448]];
const WADE_UNTEN_1: Linie = [[88, 450], [92, 454], [96, 448]];
const WADE_UNTEN_2: Linie = [[96, 448], [101, 454], [108, 450]];

export const HINTEN: Formen = {
  trapez_oben: ['M110,62 C108,72 98,80 76,88 C92,90 106,93 118,95 L118,64 C115,63 112,62 110,62 Z'],
  trapez_mitte: ['M87,94 C97,97 108,99 118,100 L118,114 C110,112 103,109 98,107 C93,105 89,102 86,98 Z'],
  trapez_unten: ['M113,115 C115,116 117,117 118,118 L118,172 C116,162 113,150 112,138 C111,128 112,121 113,115 Z'],
  rhomboiden: ['M105,109 C108,113 110,120 111,128 L111,142 C108,136 106,126 104,117 Z'],
  schulter_seite: [schulterSeite],
  schulter_hinten: [schulterFront],
  rotatoren: ['M88,103 C94,102 99,105 103,110 C105,118 103,128 99,134 C94,134 89,130 86,125 C85,117 86,109 88,103 Z'],
  teres_major: ['M86,129 C90,133 95,136 100,138 C97,145 92,149 86,151 C84,144 84,136 86,129 Z'],
  latissimus: ['M82,120 C84,133 86,144 90,152 C97,155 104,158 110,163 L110,206 C104,214 98,222 89,230 C89,214 88,196 87,180 C85,160 83,140 82,120 Z'],
  rueckenstrecker: ['M112,172 C115,173 117,175 118,177 L118,246 C115,244 112,238 110,230 C109,212 110,192 112,172 Z'],
  trizeps_lang: [oberarmInnen],
  trizeps_seitlich: [oberarmAussen],
  unterarm_strecker: unterarm,
  gesaess_mittel: [flaeche([[84, 250], [84, 241], [90, 235], [100, 232], [110, 236], [108, 248]], [[108, 248], [96, 247], [84, 250]])],
  gesaess_gross: [flaeche(GESAESS_OBEN, GESAESS_AUSSEN, GESAESS_UNTEN, [[115.5, 302], [118.5, 251]])],
  beinbeuger: [
    flaeche([[80, 296], [90, 303]], BEUGER_TEIL_1, [[94, 371], [84, 370]], rueck(BEUGER_AUSSEN)),
    flaeche([[90, 303], [104, 305]], BEUGER_TEIL_2, BEUGER_TEIL_2B, [[102, 371.5], [94, 371]], rueck(BEUGER_TEIL_1)),
    flaeche(rueck(ADDUKTOR_SCHNITT), BEUGER_INNEN_2, [[110.8, 370], [102, 371.5]], rueck(BEUGER_TEIL_2B)),
  ],
  adduktoren: [flaeche([[104, 305], [115.5, 302]], BEUGER_INNEN, ADDUKTOR_SCHNITT, rueck(BEUGER_TEIL_2))],
  waden_zwilling: [
    flaeche([[83.5, 391], [97, 393]], WADE_MITTE, rueck(WADE_UNTEN_1), rueck(WADE_AUSSEN.slice(0, 5))),
    flaeche([[97, 393], [108.5, 391]], WADE_INNEN.slice(0, 5), rueck(WADE_UNTEN_2), rueck(WADE_MITTE)),
  ],
  waden_scholle: [flaeche(WADE_AUSSEN.slice(4), [[91, 464], [98, 470], [106.6, 468]], rueck(WADE_INNEN.slice(4)), rueck(WADE_UNTEN_2), rueck(WADE_UNTEN_1))],
};

/** Feine Detaillinien in Hautfarbe - nur Zeichnung, nicht anklickbar. */
export const LINIEN_VORNE: string[] = [
  'M108,87 C100,89 92,89 84,89',
  'M90,373 C92,369 100,369 102,373 C104,381 100,387 96,387 C92,387 88,381 90,373 Z',
  'M29,311 L31,321', 'M33,313 L35,323', 'M37,313 L39,322',
  'M111,216 C113,218 115,218 117,216',
];
export const LINIEN_HINTEN: string[] = [
  'M84,100 C90,98 96,100 102,104',
  'M86,380 C94,383 102,383 108,380',
  'M98,474 L99,487',
  'M29,311 L31,321', 'M33,313 L35,323', 'M37,313 L39,322',
];
